import './ScrollView.sass';

import React, {
  FC,
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import List from 'react-virtualized/dist/commonjs/List';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';

import { getLastScrollTop, isBackNavigation } from '$navigation/router';
import { ScrollViewProps, ScrollViewVirtualizedProps } from './ScrollView.interface';
import { useDesktop, useLoadMore, useScrollAnchor } from '$hooks';
import { useRoute } from 'react-router5';
import { Loader } from '$shared/components';
import useSize from '@react-hook/size';

const additionalLoader = <Loader mode="additional" />;

const getMaxWidth = (): number => {
  const wrap = document.querySelector('.App__desktop_wrap');
  if (!wrap) {
    return 600;
  }
  return parseInt(getComputedStyle(wrap).getPropertyValue('--max-width'));
};

export const ScrollView = forwardRef<HTMLDivElement, ScrollViewProps>(
  (props, outerRef) => {
    const {
      id,
      children,
      canLoadMore = false,
      onLoadMore = null,
      className = '',
      isReversedDirection,
      isLoadingMore,
      onScroll,
      footer,
    } = props;

    const { route } = useRoute();
    const isDesktop = useDesktop();
    const [padding, setPadding] = useState((window.innerWidth - getMaxWidth()) / 2);
    const paddingLeft = isDesktop ? padding + 120 : 0;
    const paddingRight = isDesktop ? padding : 0;

    const ref = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const [_, footerHeight] = useSize(footerRef);
    const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);

    const isBack = isBackNavigation();
    const st = getLastScrollTop();

    useScrollAnchor(ref, !!isReversedDirection && !!isLoadingMore);

    const handleResize = useCallback(() => {
      setPadding(Math.max(window.innerWidth - getMaxWidth(), 0) / 2);
    }, []);

    useEffect(() => {
      handleResize();

      if (isBack) {
        if (ref.current) {
          ref.current.scrollTop = st;
        }
      }

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [route]);

    useLoadMore({
      handler: () => onLoadMore && onLoadMore(),
      enabled: canLoadMore && !!onLoadMore,
      opts: {
        node: scrollElement,
        isReversedDirection,
      },
    });

    useEffect(() => {
      setScrollElement(ref.current);
    }, [ref.current]);

    return (
      <div
        className={`ScrollView ${className}`}
        style={{ marginLeft: -paddingLeft, marginRight: -paddingRight }}
      >
        <div
          className={'ScrollView__content _ScrollView'}
          ref={(element) => {
            (ref as MutableRefObject<HTMLDivElement | null>).current = element;
            if (typeof outerRef === 'function') {
              outerRef(element);
            } else {
              if (outerRef) outerRef.current = element;
            }
          }}
          id={id}
          style={{
            paddingLeft: `${paddingLeft}px`,
            paddingRight: `${paddingRight}px`,
            paddingBottom: `${footerHeight}px`,
          }}
          onScroll={onScroll}
        >
          {isLoadingMore && isReversedDirection && additionalLoader}
          {children}
          {isLoadingMore && !isReversedDirection && additionalLoader}
          <div ref={footerRef} className={`ScrollView__footer`}>
            {footer}
          </div>
        </div>
      </div>
    );
  },
);

export const ScrollViewVirtualized: FC<ScrollViewVirtualizedProps> = (props) => {
  const {
    className = '',
    canLoadMore = false,
    onLoadMore = null,
    rowCount,
    rowHeight,
    renderRow,
  } = props;
  const { route } = useRoute();

  const ref = useRef<any>(null);

  const isBack = isBackNavigation();
  const st = getLastScrollTop();

  useEffect(() => {
    if (isBack) {
      if (ref.current) {
        ref.current.scrollToPosition(st);
      }
    }
  }, [route]);

  function renderRowWrapper({ index, key, style }) {
    return (
      <div key={key} style={style}>
        {renderRow(index)}
      </div>
    );
  }

  const handleScroll = useCallback(
    ({ clientHeight, scrollHeight, scrollTop }) => {
      if (scrollTop + clientHeight * 2 >= scrollHeight) {
        onLoadMore && onLoadMore();
      }
    },
    [onLoadMore],
  );

  const listProps: any = {};
  if (!!onLoadMore && canLoadMore) {
    listProps.onScroll = handleScroll;
  }

  return (
    <div
      className={className}
      style={typeof rowHeight === 'number' ? { height: rowHeight * rowCount } : {}}
    >
      <AutoSizer>
        {({ width, height }) => (
          // @ts-ignore
          <List
            className="_ScrollView"
            ref={ref}
            rowCount={rowCount}
            rowHeight={rowHeight}
            height={height}
            width={width}
            rowRenderer={renderRowWrapper}
            {...listProps}
          />
        )}
      </AutoSizer>
    </div>
  );
};

ScrollView.displayName = 'ScrollView';
